/**
 * <scq-quiz> — SCQ skin-aging quiz. Port of kk-headless scq/quiz/QuizClient.tsx + scripts.ts.
 * Depends on window.ScqStorage (assets/scq-storage.js, loaded by layout/funnel.liquid).
 */
if (!customElements.get('scq-quiz')) {
  class ScqQuiz extends HTMLElement {
    constructor() {
      super();
      this.step = 1;
      this.answers = {};
      this.gender = 'female';
      this.pendingAdvance = null;
      this.analyzing = false;
      this.widths = { 1: 15, 2: 30, 3: 45, 4: 60, 5: 75, 6: 90, 7: 100 };
      this.onChange = this.onChange.bind(this);
      this.onClick = this.onClick.bind(this);
      this.onBack = this.onBack.bind(this);
      this.lastHandled = { radio: null, at: 0 };
    }

    /**
     * Semantic step keys → step numbers, read from the config emitted by the section
     * (`step_key` block setting). Scoring reads answers through this map, so deleting or
     * reordering step blocks in the editor cannot silently shift the meaning of an answer.
     */
    buildKeyMap() {
      var map = {};
      var steps = (this.config && this.config.steps) || [];
      steps.forEach(function (st) {
        if (st.key) map[st.key] = String(st.step);
      });
      var required = ['gender', 'age', 'concern', 'behavior', 'frustration', 'retinol', 'sun'];
      var missing = required.filter(function (k) {
        return !map[k];
      });
      if (missing.length) {
        console.warn('[scq-quiz] step_key missing for: ' + missing.join(', ') + ' — those factors will not score');
      }
      return map;
    }

    answerFor(key) {
      var step = this.keyMap[key];
      return step ? this.answers[step] : undefined;
    }

    connectedCallback() {
      this.storage = window.ScqStorage || null;
      this.config = this.readConfig();
      this.resultsUrl = (this.config && this.config.results_url) || this.dataset.resultsUrl || '';
      if (!this.resultsUrl) console.warn('[scq-quiz] results_url is not set on the section');

      this.panels = Array.prototype.slice.call(this.querySelectorAll('[data-scq-step]'));
      this.stepCount = this.panels.length || 7;
      this.progressRow = this.querySelector('[data-scq-progress-row]');
      this.progressBar = this.querySelector('[data-scq-progress]');
      this.progressFill = this.querySelector('[data-scq-progress-fill]');
      this.progressLabel = this.querySelector('[data-scq-progress-label]');
      this.backBtn = this.querySelector('[data-scq-back]');
      this.analyzingPanel = this.querySelector('[data-scq-analyzing]');
      this.ringPath = this.querySelector('[data-scq-ring-path]');
      this.percentEl = this.querySelector('[data-scq-percent]');
      this.statusEl = this.querySelector('[data-scq-status]');
      this.keyMap = this.buildKeyMap();

      this.addEventListener('change', this.onChange);
      // A radio that is already checked (user went back, then re-picked the same answer) fires no
      // `change`, so also listen for the input's own click and advance from there.
      this.addEventListener('click', this.onClick);
      if (this.backBtn) this.backBtn.addEventListener('click', this.onBack);

      if (this.storage) {
        this.storage.captureAttributionParams();
        this.storage.track('page_view', { page: 'scq-quiz' });
        this.storage.track('quiz_start', { page: 'quiz' });
      }

      this.setGender(this.gender);
      this.render(false);
    }

    disconnectedCallback() {
      this.removeEventListener('change', this.onChange);
      this.removeEventListener('click', this.onClick);
      if (this.backBtn) this.backBtn.removeEventListener('click', this.onBack);
      if (this.pendingAdvance) clearTimeout(this.pendingAdvance);
    }

    readConfig() {
      var el = this.querySelector('[data-scq-quiz-config]');
      if (!el) return null;
      try {
        return JSON.parse(el.textContent);
      } catch (e) {
        console.warn('[scq-quiz] invalid step config JSON', e);
        return null;
      }
    }

    /* ---------- events ---------- */
    onChange(evt) {
      var radio = evt.target;
      if (!radio || !radio.matches('input[type="radio"][data-scq-answer]') || !radio.checked) return;
      this.handleAnswer(radio);
    }

    onClick(evt) {
      // Only the input's own click (dispatched after the label activates it) — never the label's,
      // so a fresh selection isn't processed twice.
      var radio = evt.target;
      if (!radio || !radio.matches || !radio.matches('input[type="radio"][data-scq-answer]')) return;
      if (!radio.checked) return;
      if (this.lastHandled.radio === radio && Date.now() - this.lastHandled.at < 250) return; // change already handled it
      this.handleAnswer(radio);
    }

    handleAnswer(radio) {
      if (this.analyzing) return;
      var panel = radio.closest('[data-scq-step]');
      if (!panel) return;
      var step = Number(panel.getAttribute('data-scq-step'));
      if (step !== this.step) return;
      this.lastHandled = { radio: radio, at: Date.now() };

      var answer = radio.value;
      this.answers[String(step)] = answer;

      if (String(step) === (this.keyMap.gender || '1')) this.setGender(answer === 'male' ? 'male' : 'female');

      if (this.storage) {
        this.storage.track('quiz_step', {
          step: step,
          answer: answer,
          question: panel.getAttribute('data-question') || '',
        });
      }

      if (this.pendingAdvance) clearTimeout(this.pendingAdvance);
      if (step >= this.stepCount) {
        this.startAnalyzing();
        return;
      }
      var self = this;
      this.pendingAdvance = setTimeout(function () {
        self.pendingAdvance = null;
        self.goTo(step + 1);
      }, 600);
    }

    onBack() {
      if (this.analyzing || this.step <= 1) return;
      if (this.pendingAdvance) {
        clearTimeout(this.pendingAdvance);
        this.pendingAdvance = null;
      }
      this.goTo(this.step - 1);
    }

    /* ---------- rendering ---------- */
    goTo(step) {
      this.step = Math.max(1, Math.min(this.stepCount, step));
      this.render(true);
    }

    render(focusPanel) {
      var self = this;
      var pct = this.widths[this.step] || 15;
      this.panels.forEach(function (panel) {
        var isCurrent = Number(panel.getAttribute('data-scq-step')) === self.step;
        panel.hidden = !isCurrent;
        if (isCurrent) {
          // restart the fadeInUp animation on every step change
          panel.style.animation = 'none';
          void panel.offsetWidth; // eslint-disable-line no-void
          panel.style.animation = '';
          if (focusPanel) panel.focus({ preventScroll: false });
        }
      });
      if (this.progressFill) this.progressFill.style.width = pct + '%';
      if (this.progressBar) this.progressBar.setAttribute('aria-valuenow', String(pct));
      if (this.progressLabel) this.progressLabel.textContent = pct + '%';
      if (this.backBtn) this.backBtn.hidden = this.step <= 1;
    }

    setGender(gender) {
      this.gender = gender;
      var imgs = this.querySelectorAll('[data-gender]');
      Array.prototype.forEach.call(imgs, function (img) {
        img.hidden = img.getAttribute('data-gender') !== gender;
      });
    }

    /* ---------- scoring (verbatim port of determineSkinType, spec §2.3) ---------- */
    determineSkinType(answers) {
      var scores = {
        'collagen-depletion': 0,
        'oxidation-damage': 0,
        'barrier-depletion': 0,
        'texture-irregularity': 0,
      };
      // Read by semantic key (see buildKeyMap) — never by hardcoded step position.
      var self = this;
      var pick = function (key) {
        var step = self.keyMap[key];
        return step ? answers[step] : undefined;
      };
      var age = pick('age');
      var concern = pick('concern');
      var behavior = pick('behavior');
      var frustration = pick('frustration');
      var retinol = pick('retinol');
      var sun = pick('sun');

      // AGE
      if (age === '70+') {
        scores['collagen-depletion'] += 2;
      } else if (age === '60-69') {
        scores['collagen-depletion'] += 1;
      } else if (age === '40-49') {
        if (sun === 'moderate' || sun === 'significant' || sun === 'extensive') {
          scores['oxidation-damage'] += 1;
        }
      }

      // CONCERN (+3)
      if (concern === 'wrinkles' || concern === 'sagging') {
        scores['collagen-depletion'] += 3;
      } else if (concern === 'spots') {
        scores['oxidation-damage'] += 3;
      } else if (concern === 'dryness') {
        scores['barrier-depletion'] += 3;
      } else if (concern === 'dullness' || concern === 'texture') {
        scores['texture-irregularity'] += 3;
      }

      // BEHAVIOR
      if (behavior === 'dry') {
        scores['barrier-depletion'] += 2;
      } else if (behavior === 'oily') {
        scores['texture-irregularity'] += 2;
      } else if (behavior === 'combination') {
        scores['texture-irregularity'] += 1;
      } else if (behavior === 'sensitive') {
        scores['barrier-depletion'] += 2;
      }

      // FRUSTRATION
      if (frustration === 'irritates') {
        scores['barrier-depletion'] += 1;
      } else if (frustration === 'not-for-my-age') {
        scores['collagen-depletion'] += 1;
      } else if (frustration === 'doesnt-work') {
        scores['texture-irregularity'] += 1;
      } else if (frustration === 'too-complicated') {
        scores['texture-irregularity'] += 1;
      }

      // RETINOL
      if (retinol === 'sometimes') {
        scores['barrier-depletion'] += 1;
      } else if (retinol === 'cant') {
        scores['barrier-depletion'] += 1;
      } else if (retinol === 'never') {
        scores['barrier-depletion'] += 0.5;
      }

      // SUN
      if (sun === 'moderate') {
        scores['oxidation-damage'] += 1;
      } else if (sun === 'significant') {
        scores['oxidation-damage'] += 2;
      } else if (sun === 'extensive') {
        scores['oxidation-damage'] += 2;
        scores['collagen-depletion'] += 1;
      }

      // TIEBREAKER (+0.1 toward the stated concern)
      var concernMap = {
        wrinkles: 'collagen-depletion',
        sagging: 'collagen-depletion',
        spots: 'oxidation-damage',
        dryness: 'barrier-depletion',
        dullness: 'texture-irregularity',
        texture: 'texture-irregularity',
      };
      if (concern && concernMap[concern]) scores[concernMap[concern]] += 0.1;

      var sorted = Object.keys(scores)
        .map(function (k) {
          return [k, scores[k]];
        })
        .sort(function (a, b) {
          return b[1] - a[1];
        });

      return {
        primary: sorted[0][0],
        secondary: sorted[1][1] >= 3 ? sorted[1][0] : null,
        scores: scores,
      };
    }

    /* ---------- analyzing screen ---------- */
    startAnalyzing() {
      if (this.analyzing) return;
      this.analyzing = true;

      var result = this.determineSkinType(this.answers);
      var state = { primary: result.primary, secondary: result.secondary, answers: this.answers };
      var storage = this.storage;
      var stored = false;
      if (storage) {
        stored = storage.writeQuizState(state);
        storage.elevarPush({ event: 'dl_complete_quiz', event_properties: { quiz_result: result.primary } });
        storage.track('quiz_complete', { skin_type: result.primary, secondary_type: result.secondary || null });
      }

      var target = this.resultsUrl || '/';
      if (storage && (!stored || !storage.storageAvailable())) {
        target = storage.appendQuizStateToUrl(this.resultsUrl || '/', state);
      }

      // swap UI
      if (this.progressRow) this.progressRow.hidden = true;
      this.panels.forEach(function (p) {
        p.hidden = true;
      });
      if (this.analyzingPanel) this.analyzingPanel.hidden = false;

      // checklist reveal (0 / 1.5 / 3.5 / 6 / 8.5s — same as headless; item 5 lands after redirect)
      var self = this;
      if (self.statusEl) self.statusEl.textContent = 'Analyzing your responses';
      [0, 1500, 3500, 6000, 8500].forEach(function (delay, i) {
        setTimeout(function () {
          var li = self.querySelector('[data-scq-check="' + (i + 1) + '"]');
          if (li) {
            li.classList.add('is-active');
            // announce milestones only (the % readout is aria-hidden and updates every frame)
            if (self.statusEl) self.statusEl.textContent = li.textContent.trim();
          }
        }, delay);
      });

      // ring animation, 7.2s, then hard navigation
      var duration = 7200;
      var totalLength = 307.919;
      var start = null;
      var navigated = false;
      var finish = function () {
        if (navigated) return;
        navigated = true;
        window.location.href = target;
      };
      var animate = function (ts) {
        if (start === null) start = ts;
        var progress = Math.min((ts - start) / duration, 1);
        var pct = Math.floor(progress * 100);
        if (self.ringPath) self.ringPath.style.strokeDashoffset = String(totalLength - progress * totalLength);
        if (self.percentEl) self.percentEl.textContent = pct + '%';
        if (progress < 1) {
          window.requestAnimationFrame(animate);
        } else {
          finish();
        }
      };
      window.requestAnimationFrame(animate);
      // safety net if rAF is throttled (background tab)
      setTimeout(finish, duration + 1500);
    }
  }

  customElements.define('scq-quiz', ScqQuiz);
}
